// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  CheckCircleOutline,
  ClearRounded,
  DoNotDisturbAltOutlined,
  EditOutlined,
  InfoOutlined,
  ListRounded,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort } from '@/constants/sort';
import { ExchangeOrderStatus } from '@/exchange-order/api/types';
import { ExchangeOrderTable } from '@/exchange-order/components/exchange-order-table';
import { ExchangeOrderTableSkeleton } from '@/exchange-order/components/exchange-order-table-skeleton';
import {
  ExchangeOrderSearchListing,
  ExchangeOrderSortProperty,
} from '@/exchange-order/hooks/use-exchange-orders';
import { useDisclosure } from '@/hooks/use-disclosure';
import { CreateOrderDialog } from '@/order/components/create-order-dialog';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * Navigation options for the exchange order listing.
 */
interface NavigateOptions {
  to?: string;
  search?: Partial<ExchangeOrderSearchListing> & Record<string, unknown>;
}

interface ExchangeOrderListingProps {
  currentStatus: ExchangeOrderStatus;
  searchParameters: ExchangeOrderSearchListing;
  sort: Sort<ExchangeOrderSortProperty>;
  onSort: (property: ExchangeOrderSortProperty) => void;
  navigateFn: (options: NavigateOptions) => void;
}

const STATUS_FILTER_OPTIONS: FilterOption<ExchangeOrderStatus>[] = [
  {
    value: 'all',
    label: 'All',
    icon: <ListRounded fontSize="small" />,
  },
  {
    value: 'draft',
    label: 'Draft',
    icon: <EditOutlined fontSize="small" />,
  },
  {
    value: 'in_progress',
    label: 'In progress',
    icon: <SyncOutlined fontSize="small" />,
  },
  {
    value: 'complete',
    label: 'Complete',
    icon: <CheckCircleOutline fontSize="small" />,
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    icon: <DoNotDisturbAltOutlined fontSize="small" />,
  },
];

export function ExchangeOrderListing(props: ExchangeOrderListingProps) {
  const { navigateFn, searchParameters, currentStatus, sort, onSort } = props;
  const searchContainer = useRef<HTMLInputElement>(null);
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const createDialog = useDisclosure();

  /**
   * Debounced search query handler.
   */
  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigateFn({
          search: { ...omit(searchParameters, 'page', 'query') },
        });
        return;
      }
      navigateFn({
        search: { ...omit(searchParameters, 'page'), query },
      });
    }, 300);
  }, [navigateFn, searchParameters]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  /**
   * Handle status toggle change.
   */
  const handleStatusChange = (newStatus: ExchangeOrderStatus) => {
    const basePath = '/exchanges';
    if (newStatus === 'all') {
      navigateFn({
        to: basePath,
        search: {
          ...searchParameters,
          page: undefined,
          status: undefined,
        },
      });
    } else {
      navigateFn({
        to: basePath,
        search: {
          ...searchParameters,
          page: undefined,
          status: newStatus,
        },
      });
    }
  };

  const onPageChange = (pageNumber: number) => {
    navigateFn({
      search: { ...searchParameters, page: pageNumber },
    });
  };

  /**
   * Handle search input changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Clear the search query.
   */
  const handleClearQuery = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    updateQuery('');
  };

  const pageNumber = searchParameters.page ?? 1;

  return (
    <Stack>
      <CreateOrderDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
        defaultOrderType="exchange"
      />

      {/* Action bar with create button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={currentStatus}
            options={STATUS_FILTER_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel="exchange status filter"
          />
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Learn about exchanges"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>

        {/* Educational tooltip explaining exchanges */}
        <EducationalTooltip
          title="Exchanges"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Exchanges</strong> coordinate bidirectional item flows
                for customer exchanges. They link inbound return orders with
                outbound fulfillment orders to track items being both received
                back from and sent out to customer locations.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Exchange status
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Draft:</strong> Order is being prepared but not yet
                  confirmed.
                </li>
                <li>
                  <strong>In progress:</strong> Exchange is actively being
                  processed with linked return and fulfillment orders.
                </li>
                <li>
                  <strong>Complete:</strong> All items have been exchanged
                  successfully.
                </li>
                <li>
                  <strong>Cancelled:</strong> The exchange has been cancelled
                  and is no longer valid.
                </li>
              </Typography>
            </Box>
          </Stack>
        </EducationalTooltip>

        <Button
          variant="contained"
          startIcon={<AddRounded fontSize="small" />}
          onClick={createDialog.open}
        >
          Add exchange
        </Button>
      </Stack>

      {/* Search field */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}></Stack>
        <TextField
          size="small"
          inputRef={searchContainer}
          placeholder="Search exchanges"
          defaultValue={searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          InputProps={{
            endAdornment: searchParameters.query && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearQuery} edge="end" size="small">
                  <ClearRounded />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
        />
      </TableControlContainer>

      {/* Table */}
      <SupportErrorBoundary message="Failed to load exchanges">
        <Suspense fallback={<ExchangeOrderTableSkeleton />}>
          <ExchangeOrderTable
            filter={{
              status: currentStatus,
              searchTerm: searchParameters.query,
              page: pageNumber,
              pageSize: 20,
            }}
            sort={sort}
            onSort={onSort}
            onPageChange={onPageChange}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
